import { useCallback, useEffect, useRef } from 'react';
import * as Tone from 'tone';
import { midiToName } from 'AudioRecognition/util/note';
import EventStream, { type PlaybackNoteEvent } from 'Models/EventStream';
import Scheduler2 from 'Utils/Scheduler';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';

type PlaybackSamplerWindow = Window & {
  __ensureSamplerReady?: (forceRebuild?: boolean) => Promise<void>;
  __museflowSampler?: Tone.Sampler;
};

type UseRepertoireNotePlaybackOptions = {
  enabled: boolean;
  isPlaying: boolean;
  scheduler: Scheduler2 | null;
  eventStream: EventStream | null;
  intervalName: string;
};

export const prepareRepertoireNotePlaybackAudio = async () => {
  await Tone.start();
  await (window as PlaybackSamplerWindow).__ensureSamplerReady?.();
};

const MAX_FORWARD_JUMP_MEASURES = 0.75;

const useRepertoireNotePlayback = ({
  enabled,
  isPlaying,
  scheduler,
  eventStream,
  intervalName,
}: UseRepertoireNotePlaybackOptions) => {
  const synthRef = useRef<Tone.PolySynth<Tone.Synth> | null>(null);
  const activeNotesRef = useRef<Map<string, PlaybackNoteEvent>>(new Map());
  const lastTimestampRef = useRef<number | null>(null);

  const ensureSynth = useCallback(() => {
    if (synthRef.current) return synthRef.current;

    const synth = new Tone.PolySynth(Tone.Synth).toDestination();
    synth.set({
      volume: -7,
      oscillator: {
        type: 'triangle',
      },
      envelope: {
        attack: 0.01,
        decay: 0.12,
        sustain: 0.28,
        release: 0.7,
      },
    });
    synthRef.current = synth;
    return synth;
  }, []);

  const triggerAttack = useCallback(
    (midiNote: number) => {
      const playbackWindow = window as PlaybackSamplerWindow;
      const sampler = playbackWindow.__museflowSampler;
      const noteName = midiToName(midiNote);

      if (sampler) {
        sampler.triggerAttack(noteName, Tone.immediate(), 0.78);
        return;
      }

      ensureSynth().triggerAttack(
        Tone.Frequency(midiNote, 'midi').toFrequency(),
        Tone.immediate(),
        0.78,
      );
    },
    [ensureSynth],
  );

  const triggerRelease = useCallback(
    (midiNote: number) => {
      const playbackWindow = window as PlaybackSamplerWindow;
      const sampler = playbackWindow.__museflowSampler;
      const noteName = midiToName(midiNote);

      if (sampler) {
        sampler.triggerRelease(noteName, Tone.immediate());
        return;
      }

      ensureSynth().triggerRelease(
        Tone.Frequency(midiNote, 'midi').toFrequency(),
        Tone.immediate(),
      );
    },
    [ensureSynth],
  );

  const releaseActiveNote = useCallback(
    (noteId: string) => {
      const activeNote = activeNotesRef.current.get(noteId);
      if (!activeNote) return;

      activeNotesRef.current.delete(noteId);
      triggerRelease(activeNote.midiNote);
    },
    [triggerRelease],
  );

  const releaseActiveMidiNote = useCallback(
    (midiNote: number) => {
      for (const [noteId, activeNote] of activeNotesRef.current) {
        if (activeNote.midiNote === midiNote) {
          releaseActiveNote(noteId);
        }
      }
    },
    [releaseActiveNote],
  );

  const releaseAll = useCallback(() => {
    const midiNotes = Array.from(activeNotesRef.current.values()).map((note) => note.midiNote);
    activeNotesRef.current.clear();
    midiNotes.forEach((midiNote) => triggerRelease(midiNote));
    synthRef.current?.releaseAll();
    (window as PlaybackSamplerWindow).__museflowSampler?.releaseAll?.();
  }, [triggerRelease]);

  const attackPlaybackNote = useCallback(
    (note: PlaybackNoteEvent) => {
      releaseActiveMidiNote(note.midiNote);
      activeNotesRef.current.set(note.id, note);
      triggerAttack(note.midiNote);
    },
    [releaseActiveMidiNote, triggerAttack],
  );

  useEffect(() => {
    if (!enabled || !isPlaying || !scheduler || !eventStream) {
      scheduler?.clearIntervalFn(intervalName);
      releaseAll();
      lastTimestampRef.current = null;
      return;
    }

    void prepareRepertoireNotePlaybackAudio();
    scheduler.clearIntervalFn(intervalName);
    scheduler.setIntervalByTick(
      intervalName,
      () => {
        if (eventStream.timeKeeper.isPaused()) {
          releaseAll();
          lastTimestampRef.current = null;
          return;
        }

        const timestamp = eventStream.timeKeeper.audioTimeToMeasureTimestamp();
        if (!Number.isFinite(timestamp)) return;

        const lastTimestamp = lastTimestampRef.current;
        if (
          lastTimestamp == null ||
          timestamp < lastTimestamp ||
          timestamp - lastTimestamp > MAX_FORWARD_JUMP_MEASURES
        ) {
          releaseAll();
          eventStream
            .getActivePlaybackNoteEventsAtTimestamp(timestamp)
            .forEach((note) => attackPlaybackNote(note));
          lastTimestampRef.current = timestamp;
          return;
        }

        for (const [noteId, activeNote] of activeNotesRef.current) {
          if (timestamp >= activeNote.offsetTimestamp) {
            releaseActiveNote(noteId);
          }
        }

        eventStream
          .getPlaybackNoteEventsBetween(lastTimestamp, timestamp)
          .forEach((note) => attackPlaybackNote(note));
        lastTimestampRef.current = timestamp;
      },
      NoteType._64th,
      1,
    );

    return () => {
      scheduler.clearIntervalFn(intervalName);
      releaseAll();
      lastTimestampRef.current = null;
    };
  }, [
    attackPlaybackNote,
    enabled,
    eventStream,
    intervalName,
    isPlaying,
    releaseActiveNote,
    releaseAll,
    scheduler,
  ]);

  useEffect(() => {
    return () => {
      releaseAll();
      synthRef.current?.dispose();
      synthRef.current = null;
    };
  }, [releaseAll]);
};

export default useRepertoireNotePlayback;
